import { useMemo, useRef } from "react";

import { useIsMutating } from "../mutation";
import { useBaseQuery } from "./useBaseQuery";
import type { Variables, Extensions, GraphqlErrorResponse } from "../graphql";
import { includeGroup } from "../utils";
import type {
  QueryOptions,
  QueryOpts,
  RetryOnErrorOpts,
  UseBaseQueryOptions,
  UseBaseQueryResult,
  UseQueryResult,
} from "./types";
import type { QueryKey } from "../queryKey";
import { useQueryKey } from "../queryKey";
import { useGraphQLFetching } from "../client";
import type { FeapiError, GqlObj } from "../graphql/types";

const DEFAULT_RETRY_ON_ERROR = { enabled: false };

/**
 * Performs a graphql query to frontend-api. After any mutation, the query will be re-fetched
 *
 * @remarks
 * You can customize the query with graphql operation directives:
 * - \@ignore_update: Avoid perform query after mutations
 * - \@update_groups(groups: "groupName1,groupName2"): Perform query after mutations only if mutation and query have a match in update groups
 *
 * @param query - Graphql query operation, builded with gql constructor
 * @param options - Query options, included query params
 *
 * @returns Query response and current status of the query
 */
export function useQuery<
  TData,
  TVars extends Variables | undefined = undefined,
>(
  queryParam: GqlObj,
  options: QueryOptions<TData, TVars> = undefined,
): UseQueryResult<TData, TVars> {
  const { variables, updateGroups, ...opts } = options || {};
  const queryKey = useQueryKey({ query: queryParam, variables, updateGroups });
  const query = queryKey[1].query;
  const { fetcher, extensions, fetchTime } = useGraphQLFetching<TData, TVars>();
  const result = useBaseQuery<
    TData,
    GraphqlErrorResponse<TData>,
    TData,
    QueryKey<TVars>
  >(
    queryKey,
    ({ queryKey: [, data] }) => fetcher(data, data.graphQLEndpoint),
    getQueryOptions(opts),
  );

  return useQueryResults<TData, TVars>(
    result,
    queryKey,
    query,
    extensions,
    fetchTime,
  );
}

const useQueryResults = <TData, TVars extends Variables | undefined>(
  result: UseBaseQueryResult<TData, GraphqlErrorResponse<TData>>,
  queryKey: QueryKey<TVars>,
  query: GqlObj,
  extensions: Extensions | null,
  fetchTime: number | null,
): UseQueryResult<TData, TVars> => {
  const mutatingObserved = useRef(false);
  const isMutating =
    useIsMutating({
      predicate: (mutation) => {
        const mutationQuery = mutation.options.variables.query;
        return (
          mutatingObserved.current &&
          includeGroup(mutationQuery, query) &&
          !mutationQuery.directives.silent &&
          !query.directives.ignoreUpdate
        );
      },
    }) > 0;

  const {
    errors,
    data: dataWithErrors,
    status: httpStatusCode,
    headers,
  } = result.error || {};

  return useMemo<UseQueryResult<TData, TVars>>(
    () => ({
      queryKey,
      data: result.data || null,
      status: result.status,
      errors: errors || null,
      extensions,
      headers,
      fetchTime,
      httpStatusCode,
      operationName: query.operationName,
      dataWithErrors,
      loading: result.status === "loading",
      get mutating() {
        mutatingObserved.current = true;
        return isMutating;
      },
      get fetching() {
        return result.isFetching;
      },
      get backgroundFetching() {
        return result.isRefetching;
      },
    }),
    [
      queryKey,
      query.operationName,
      extensions,
      fetchTime,
      headers,
      httpStatusCode,
      dataWithErrors,
      result,
      errors,
      isMutating,
    ],
  );
};

const NON_RETRYABLE_ERROR_CODES: FeapiError["extensions"]["errorCode"][] = [
  "SESSION_EXPIRED",
];

const hasRetryByErrorCode = (
  errorCode: FeapiError["extensions"]["errorCode"],
  retryOnErrorOpts: RetryOnErrorOpts,
): boolean => {
  const isRequestError = errorCode === "REQUEST_ERROR";
  const hasDefaultRetry = isRequestError && !retryOnErrorOpts?.enabled;
  const hasRetryByConfig =
    retryOnErrorOpts?.enabled &&
    !!errorCode &&
    !retryOnErrorOpts?.discardedErrorCodes?.includes?.(errorCode) &&
    !NON_RETRYABLE_ERROR_CODES.includes(errorCode);

  return hasDefaultRetry || hasRetryByConfig;
};

const getQueryOptions = <TData>({
  skip,
  onSuccess,
  onError,
  refetchOnWindowFocus = false,
  refetchInterval = false,
  cacheTime = 300000,
  staleTime = 200,
  retryOnError = DEFAULT_RETRY_ON_ERROR,
}: QueryOpts<TData>): UseBaseQueryOptions<
  TData,
  GraphqlErrorResponse<TData>,
  TData
> => ({
  enabled: !skip,
  cacheTime,
  staleTime,
  retry(failureCount: number, { errors = [] }: GraphqlErrorResponse) {
    return (
      errors.length > 0 &&
      errors.every((e) =>
        hasRetryByErrorCode(e?.extensions?.errorCode, retryOnError),
      ) &&
      failureCount < 3
    );
  },
  notifyOnChangeProps: "tracked",
  onSuccess,
  onError,
  refetchOnWindowFocus,
  refetchInterval,
});
